import { useEffect, useState } from "react";

const routeRedirects = {
  "/visit": "/contact",
  "/visit.html": "/contact",
};

const normalizePath = (path) => {
  if (!path || path === "/index.html") {
    return "/";
  }

  const normalized = path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;

  return routeRedirects[normalized] || normalized;
};

export function useRoute({ preloadRoute } = {}) {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));

  useEffect(() => {
    const normalizedInitialPath = normalizePath(window.location.pathname);

    if (normalizedInitialPath !== window.location.pathname) {
      window.history.replaceState(
        {},
        "",
        `${normalizedInitialPath}${window.location.search}${window.location.hash}`,
      );
    }

    const navigate = (nextPath, hash = "") => {
      const normalized = normalizePath(nextPath);

      if (normalized === normalizePath(window.location.pathname)) {
        return;
      }

      window.history.pushState({}, "", `${normalized}${hash}`);
      setPath(normalized);

      if (hash) {
        window.requestAnimationFrame(() => {
          document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    const getLinkFromEvent = (event) => {
      const target =
        event.target instanceof Element ? event.target : event.target?.parentElement;

      return target?.closest?.("a");
    };

    const getInternalUrl = (link) => {
      if (
        !link ||
        link.target ||
        link.hasAttribute("download")
      ) {
        return null;
      }

      const url = new URL(link.href, window.location.origin);

      if (url.origin !== window.location.origin || !url.pathname.startsWith("/")) {
        return null;
      }

      return url;
    };

    const handleLinkIntent = (event) => {
      const url = getInternalUrl(getLinkFromEvent(event));

      if (url) {
        preloadRoute?.(normalizePath(url.pathname));
      }
    };

    const handleClick = (event) => {
      if (
        event.defaultPrevented ||
        (typeof event.button === "number" && event.button !== 0) ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const url = getInternalUrl(getLinkFromEvent(event));

      if (!url) {
        return;
      }

      const currentPath = normalizePath(window.location.pathname);
      const nextPath = normalizePath(url.pathname);

      if (nextPath === currentPath && url.hash) {
        return;
      }

      preloadRoute?.(nextPath);
      event.preventDefault();
      navigate(nextPath, url.hash);
    };

    const handlePopState = () => {
      setPath(normalizePath(window.location.pathname));
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    document.addEventListener("pointerover", handleLinkIntent, { passive: true });
    document.addEventListener("focusin", handleLinkIntent);
    document.addEventListener("touchstart", handleLinkIntent, { passive: true });
    document.addEventListener("click", handleClick);
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener("pointerover", handleLinkIntent);
      document.removeEventListener("focusin", handleLinkIntent);
      document.removeEventListener("touchstart", handleLinkIntent);
      document.removeEventListener("click", handleClick);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [preloadRoute]);

  return path;
}
